/*!
 * Copyright (c) 2018, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { take, takeUntil } from 'rxjs/operators';
import { UnsubscribeOnDestroy } from 'src/app/core/classes/UnsubscribeOnDestroy';
import { UpdateAccountInfoModel } from '../../models/update-account-info.model';
import { UserProfileService } from '../../services/user-profile.service';
import { ResetPasswordDialogComponent } from '../dialogs/reset-password-dialog/reset-password-dialog.component';
import { UploadPhotoDialogComponent } from '../dialogs/upload-photo-dialog/upload-photo-dialog.component';
import * as UserAction from '../../../../store/user/user.actions';
import { BaseErrorModel } from 'src/app/core/models/base-error.model';
import { MessageBarConfig, MessageBarStyle } from 'src/app/shared/components/message-bar/message-bar.component';
import { EmailModel } from 'src/app/features/management/account-management/models/email.model';
import { deepEqual, getAdvancedEmailValidator } from '@shared/helpers/utils';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';

interface Claim {
  claim: string;
  value: string;
}

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent extends UnsubscribeOnDestroy implements OnInit {
  user: any;
  initialUser: any;

  userData: FormGroup;

  displayedMessage: MessageBarConfig;
  isError = false;

  isUserDataInitial$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(
    private store: Store<any>,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    private userProfileService: UserProfileService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.store.select('user')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((state) => this.initialize(state));

    this.userData.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        this.isUserDataInitial$.next(deepEqual(this.initialUser, {name: value.name, email: value.email}));
      });
  }

  private initialize(state): void {
    this.user = {...state};
    this.initialUser = {...{name: state.name, email: state.email}};

    if (!this.userData) {
      this.initForm();
    }
  }

  private initForm(): void {
    this.userData = this.formBuilder.group({
      name: new FormControl(this.user.name, [
        Validators.required,
        Validators.pattern(/^[a-zA-Z'.-]+( [a-zA-Z'.-]+)*$/),
      ]),
      email: new FormControl(this.user.email, [
        Validators.required,
        Validators.email,
        getAdvancedEmailValidator,
      ]),
      // Since we can't get the user's password directly,
      // we use this string only for appearance in readonly password field
      password: new FormControl('SuperSecretPassword_xD'),
    });
  }

  saveProfileData(): void {
    if (this.userData.invalid) {
      return;
    }

    const model = this.getModelFromForm();

    if (!model) {
      return;
    }

    if (model.email !== this.user.email) {
      const emailModel: EmailModel = {email: model.email};

      this.userProfileService.checkIfEmailExist(emailModel).subscribe(
        (isExist) => {
          if (isExist) {
            this.userData.controls.email.setErrors({error: 'Email already in use'});
            return;
          } else {
            this.updateAccount(model);
          }
        },
        (error) => {
          console.error(error);
        }
      );
    } else {
      this.updateAccount(model);
    }
  }

  private getModelFromForm(): UpdateAccountInfoModel {
    let name: string[];

    try {
      name = this.userData.controls.name.value.split(' ');

      if (name?.length !== 2) {
        this.userData.controls.name.setErrors({'Name Is Invalid': true});
        return undefined;
      }
    } catch (e) {
      return undefined;
    }

    return {
      firstName: name[0],
      lastName: name[1],
      email: this.userData.controls.email.value,
    };
  }

  removePhoto(): void {
    this.userProfileService.deletePhoto(this.user.id)
      .pipe(take(1))
      .subscribe(
        (res) => {
          this.displayedMessage = {
            message: 'Your profile photo has been removed.',
            style: MessageBarStyle.success,
          };

          this.store.dispatch(new UserAction.SetUser({
            ...this.user,
            img: undefined,
          }));
        },
        (err) => {
          this.displayedMessage = {
            message: 'An error occurred while deleting profile photo.',
            style: MessageBarStyle.error,
          };
        });
  }

  changePhoto(): void {
    this.dialog.open(UploadPhotoDialogComponent, {panelClass: 'uploadPhotoDialog'})
      .afterClosed()
      .subscribe((photo) => {
        this.displayedMessage = undefined;

        if (!photo) {
          return;
        }

        this.userProfileService.updateProfilePhoto(this.user.id, photo.file)
          .subscribe(
            (res) => {
              this.displayedMessage = {
                message: 'Your profile photo has been saved.',
                style: MessageBarStyle.success,
              };

              this.store.dispatch(new UserAction.SetUser({
                ...this.user,
                img: photo.base64,
              }));
            },
            (err: BaseErrorModel) => {
              this.displayedMessage = {
                message: 'An error occurred while saving profile photo.',
                style: MessageBarStyle.error,
              };
            });
      });
  }

  resetPassword(): void {
    this.dialog.open(ResetPasswordDialogComponent, {data: this.user})
      .afterClosed()
      .subscribe((res) => {
        this.displayedMessage = undefined;
        if (res) {
          this.displayedMessage = {
            message: 'Your password has been updated.',
            style: MessageBarStyle.success,
          };
        }
      });
  }

  private updateAccount(model: UpdateAccountInfoModel): void {
    this.userProfileService.updateAccount(this.user.id, model)
      .pipe(take(1))
      .subscribe(
        (res) => {
          this.isError = false;
          this.displayedMessage = {
            message: 'Your profile has been saved.',
            style: MessageBarStyle.success,
          };
          this.user.name = model.firstName + ' ' + model.lastName;
          this.user.email = model.email;
          this.initialUser = {...{name: this.user.name, email: this.user.email}};
          this.isUserDataInitial$.next(true);
          this.store.dispatch(new UserAction.SetUserInfo(this.user));
        },
        (err) => {
          this.isError = true;
          this.displayedMessage = {
            message: 'An error occurred while saving profile info.',
            style: MessageBarStyle.error,
          };
        });
  }
}
